import { useMemo, useState } from 'react';

import { format, parseISO } from 'date-fns';
import { CheckCircle, Users, XCircle } from 'lucide-react';

import type { Subscription } from '@/types/service/internalAnalytics';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface SubscriptionSummaryProps {
  subscriptions: Subscription[];
}

type FilterType = 'all' | 'active' | 'expired';

const SubscriptionSummary = ({ subscriptions }: SubscriptionSummaryProps) => {
  const [selectedFilter, setSelectedFilter] = useState<FilterType | null>(null);

  // Calculate counts
  const { activeCount, expiredCount, totalCount } = useMemo(() => {
    let active = 0;
    let expired = 0;

    for (const sub of subscriptions) {
      if (sub.disabled) {
        expired++;
      } else {
        active++;
      }
    }

    return { activeCount: active, expiredCount: expired, totalCount: subscriptions.length };
  }, [subscriptions]);

  // Filter and sort subscriptions
  const filteredSubscriptions = useMemo(() => {
    let filtered = [...subscriptions];

    if (selectedFilter === 'active') {
      filtered = filtered.filter((sub) => !sub.disabled);
    } else if (selectedFilter === 'expired') {
      filtered = filtered.filter((sub) => sub.disabled);
    }

    // Sort by end_at descending (most recent first)
    return filtered.sort((a, b) => {
      const dateA = a.end_at ? new Date(a.end_at).getTime() : 0;
      const dateB = b.end_at ? new Date(b.end_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [subscriptions, selectedFilter]);

  const formatDate = (isoDate: string | null) => {
    if (!isoDate) return '-';
    try {
      return format(parseISO(isoDate), 'MMM dd, yyyy');
    } catch {
      return isoDate;
    }
  };

  const handleCardClick = (filter: FilterType) => {
    if (selectedFilter === filter) {
      setSelectedFilter(null);
    } else {
      setSelectedFilter(filter);
    }
  };

  const stats = [
    {
      key: 'active' as FilterType,
      label: 'Active',
      value: activeCount,
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'ring-emerald-500',
    },
    {
      key: 'expired' as FilterType,
      label: 'Expired',
      value: expiredCount,
      icon: XCircle,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      borderColor: 'ring-gray-500',
    },
    {
      key: 'all' as FilterType,
      label: 'Total',
      value: totalCount,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'ring-blue-500',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Free Trial Summary</CardTitle>
      </CardHeader>
      <CardContent className='space-y-6'>
        {/* Summary Cards */}
        <div className='grid grid-cols-3 gap-4'>
          {stats.map((stat) => {
            const Icon = stat.icon;
            const isSelected = selectedFilter === stat.key;

            return (
              <button
                key={stat.key}
                onClick={() => handleCardClick(stat.key)}
                className={`
                  p-4 rounded-lg border transition-all text-left
                  hover:shadow-md cursor-pointer
                  ${isSelected ? `ring-2 ${stat.borderColor} shadow-md` : 'hover:border-gray-300'}
                `}
              >
                <div className='flex items-center gap-3'>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className='text-2xl font-bold'>{stat.value.toLocaleString()}</p>
                    <p className='text-sm text-muted-foreground'>{stat.label}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <p className='text-sm text-muted-foreground'>Click a card above to view details</p>

        {/* Data Table */}
        {selectedFilter && (
          <div className='border rounded-lg'>
            <div className='p-4 border-b bg-muted/30'>
              <h4 className='font-semibold'>
                {selectedFilter === 'all'
                  ? 'All'
                  : selectedFilter === 'active'
                    ? 'Active'
                    : 'Expired'}{' '}
                Trials
                <span className='ml-2 text-muted-foreground font-normal'>
                  ({filteredSubscriptions.length}{' '}
                  {filteredSubscriptions.length === 1 ? 'trial' : 'trials'})
                </span>
              </h4>
            </div>
            <div className='overflow-x-auto max-h-[400px] overflow-y-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Institution</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Ends</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscriptions.map((sub) => (
                    <TableRow key={sub.subscription_id}>
                      <TableCell className='font-medium'>
                        {sub.first_name} {sub.last_name}
                      </TableCell>
                      <TableCell>{sub.email}</TableCell>
                      <TableCell>{sub.institution_name || '-'}</TableCell>
                      <TableCell>{formatDate(sub.started_at)}</TableCell>
                      <TableCell>{formatDate(sub.end_at)}</TableCell>
                      <TableCell>
                        <Badge variant={sub.disabled ? 'secondary' : 'default'}>
                          {sub.disabled ? 'Expired' : 'Active'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionSummary;
